import React, { useContext, useMemo, useCallback, useRef } from 'react';
import classNames from 'classnames';
import Animation from 'components/Animation';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Overlay } from 'ncoded-component-library';
import NotificationsContext from 'providers/Notifications/Notifications.context';
import ThreeDotsDropdown from 'components/ThreeDotsDropdown';
import NotificationCard from 'components/NotificationCard';
import InfiniteScroll from 'components/InfiniteScroll';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import {
  NotificationsActionsPlaceholder,
  NotificationsPlacholder,
} from '../NotificationCard/placeholder';
import ArrowsexpandIcon from 'icons/Arrowsexpand.icon';
import XIcon from 'icons/X.icon';
import {
  NOTIFICATIONS_ROUTE,
  PROFILE_PERSONAL_DETAILS_ROUTE,
} from 'data/global';
import NotificationUnreadToggle from './components/NotificationUnreadToggle';
import NotificationGroupSelect from './components/NotificationGroupSelect';

import './NotificationsDrawer.styles.scss';
import './NotificationsDrawer.styles.responsive.scss';

type NotificationsDrawerProps = {
  className?: string;
};
const NotificationsDrawer: React.FC<NotificationsDrawerProps> = (props) => {
  const { className } = props;

  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    numberOfPlaceholders,
    isDrawerOpen,
    notifications,
    fetchingNotifications,
    setIsDrawerOpen,
    readAllNotifications,
    onNotificationsScroll,
    redirectionPath,
    getDynamicRedirectionPath,
    onDeleteNotification,
    onReadNotification,
    onUnreadNotification,
    getCtaIcon,
    getCtaPrimary,
    getCtaSecondary,
    getCtaText,
    getContent,
    getIsLoadingNotification,
  } = useContext(NotificationsContext);

  const classes = classNames(
    'notifications-drawer',
    {
      'notification-drawer--open': isDrawerOpen,
    },
    className,
  );

  const threeDotsRef = useRef<DropdownRef>(null);

  const emptyPlaceholder = useMemo(
    () => (
      <div className="notifications-drawer__placeholder">
        {fetchingNotifications &&
          Array(numberOfPlaceholders)
            .fill(1)
            .map((_, i) => <NotificationsPlacholder key={i} />)}
      </div>
    ),
    [fetchingNotifications, numberOfPlaceholders],
  );

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    threeDotsRef.current?.setIsOpen(false);
  }, [setIsDrawerOpen]);

  return (
    <>
      {isDrawerOpen && (
        <Overlay
          className="notifications-drawer__overlay"
          open
          noContent
          overlayCloses
          lockScroll
          onClose={closeDrawer}
        />
      )}

      <Animation
        open={isDrawerOpen}
        duration={400}
        timingFunction="ease-in-out"
        appearAnimation="moveInRight"
        disappearAnimation="moveOutRight"
      >
        <InfiniteScroll
          localScroll
          className={classes}
          onScroll={onNotificationsScroll}
        >
          <div className="notifications-drawer__header">
            <div className="notifications-drawer__header__title">
              <p>{t('notifications')}</p>
              <Button
                icon={ArrowsexpandIcon}
                variant="icon"
                aria-label="Show all notifications."
                className="expand"
                onClick={() => {
                  closeDrawer();
                  navigate(NOTIFICATIONS_ROUTE);
                }}
              />
            </div>

            <NotificationUnreadToggle />

            <Button
              variant="icon"
              icon={XIcon}
              aria-label="Close"
              onClick={closeDrawer}
            />
          </div>

          <div className="notifications-drawer__actions-container">
            {!fetchingNotifications ? (
              <>
                <NotificationGroupSelect />
                <ThreeDotsDropdown
                  ref={threeDotsRef}
                  className="notifications-drawer__actions"
                  options={[
                    {
                      label: t('markAllAsRead'),
                      onClick: () => readAllNotifications(),
                    },
                    {
                      label: t('notificationSettings'),
                      onClick: () => {
                        navigate(PROFILE_PERSONAL_DETAILS_ROUTE);
                        closeDrawer();
                      },
                    },
                  ]}
                />
              </>
            ) : (
              <NotificationsActionsPlaceholder />
            )}
          </div>

          {!fetchingNotifications && !notifications.length ? (
            <div className="notifications-drawer__empty-state">
              <p>{t('youHaveNoNotificationsYet')}</p>
            </div>
          ) : (
            notifications.map((notif) => (
              <NotificationCard
                key={notif.id}
                notification={notif}
                loading={getIsLoadingNotification(notif._id)}
                to={redirectionPath(notif)}
                onDynamicTo={getDynamicRedirectionPath(notif)}
                onRead={onReadNotification}
                onUnread={onUnreadNotification}
                onDelete={onDeleteNotification}
                ctaPrimary={getCtaPrimary(notif)}
                ctaSecondary={getCtaSecondary(notif)}
                ctaText={getCtaText(notif)}
                ctaIcon={getCtaIcon(notif)}
                contentText={getContent(notif)}
              />
            ))
          )}

          {emptyPlaceholder}
        </InfiniteScroll>
      </Animation>
    </>
  );
};

export default React.memo(NotificationsDrawer);
